import {
  Avatar,
  Badge,
  Box,
  Button,
  DropdownButton,
  FlexBox,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { AiDraftType } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { SourceConversationsChip } from 'lib-knowledge-utils/components/sourceConversations/index';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useState } from 'react';

import { useAiDraftActivityLogRenderer } from '../aiDraftActivityLog';
import { useSuggestionActions } from '../hooks/useSuggestionActions';
import { Suggestion } from '../utils/toSuggestion';

import { ArticleComparisonModal } from './articleComparisonModal';
import { SuggestionActions } from './suggestionActions';
import { SuggestionActivityLog } from './suggestionActivityLog';
import { SuggestionFiling } from './suggestionFiling';
import s from './suggestionPreview.module.scss';
import { TYPE_LABEL } from './suggestionTypeBadge';

type SuggestionPreviewProps = {
  suggestion: Suggestion;
  /** Approved or discarded — the draft is gone and nothing should stay selected. */
  onRemoved: () => void;
};

/**
 * The selected draft, as the reviewer reads it: what kind of draft it is, where
 * it would be filed, who asked for it and from which conversations, then the
 * body itself.
 *
 * A plain function of the draft it is given: `SelectedSuggestionPreview`
 * resolves the draft by id and hands it over, so this holds no query of its own
 * and can be rendered against any `Suggestion`.
 */
export function SuggestionPreview({
  suggestion,
  onRemoved,
}: SuggestionPreviewProps): JSX.Element {
  const { showModal } = useModal();
  const {
    type,
    title,
    htmlContent,
    summary,
    collectionName,
    categoryName,
    conversations,
    initiatedBy,
  } = suggestion;
  const actions = useSuggestionActions({ suggestion, onRemoved });
  const [isFiling, setIsFiling] = useState(false);
  // Swaps the body for the draft's history, as the conversation header's
  // button swaps the messages for theirs. The preview is keyed by draft, so
  // choosing another suggestion starts from its body again.
  const renderActivityLog = useAiDraftActivityLogRenderer();
  const [isShowingActivity, setIsShowingActivity] = useState(false);

  // Only an update draft has a previous version to compare against, and only
  // one that still names an article can resolve it.
  const canCompare =
    type === AiDraftType.ArticleUpdate && !!suggestion.forArticleId;

  // The article an update draft rewrites, for a reviewer who wants it in
  // context rather than only as the comparison's diff. In a new tab, so their
  // place in the queue survives it.
  const articleHref =
    type === AiDraftType.ArticleUpdate && suggestion.forArticleId
      ? routeGenerator.articleEdit({
          articleId: suggestion.forArticleId,
          collectionId: suggestion.collectionId,
          languageId: suggestion.languageId,
        })
      : undefined;

  // Only a new draft chooses where it goes. An update reports the filing of the
  // article it writes back to, which is the article's and not the draft's to
  // change, so it is shown without the pencil.
  const canRefile = type === AiDraftType.NewArticle;

  // `filter(Boolean)` rather than `&&` between the two: a new draft can have a
  // collection but no category, and either name can fail to resolve.
  const breadcrumb = [collectionName, categoryName].filter(Boolean).join(' · ');

  return (
    <FlexBox
      flexDirection="column"
      flex="1"
      height="100%"
      minHeight="0"
      background="neutral.000"
    >
      <Box flex="1" minHeight="0" padding="500" className={s.scroll}>
        <FlexBox
          flexDirection="column"
          gap="250"
          background="purple.100"
          borderColor="purple.400"
          borderRadius="100"
          padding="250 300"
        >
          {/* Chip height. `View comparison` is a 32px button and only an
              update draft has one, which would otherwise start that draft's
              title 8px lower than a new one's. The button's padding is empty
              space, so it centres on the row and overhangs it unnoticed. */}
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            gap="200"
            height="24px"
          >
            <Badge
              variant="primary"
              size="small"
              style={{
                backgroundColor: 'var(--dixa-color-neutral-000)',
                borderColor: 'var(--dixa-color-neutral-000)',
              }}
            >
              {TYPE_LABEL[type]}
            </Badge>

            <FlexBox alignItems="center" gap="200">
              {canCompare && (
                <Button
                  variant="tertiary"
                  onClick={() =>
                    showModal(ArticleComparisonModal, { suggestion })
                  }
                >
                  View comparison
                </Button>
              )}
              {initiatedBy && (
                <Tooltip content={`Initiated by: ${initiatedBy.name}`}>
                  <Avatar
                    size="24"
                    user={{
                      name: initiatedBy.name,
                      email: initiatedBy.email,
                      avatar_url: initiatedBy.avatarUrl,
                    }}
                  />
                </Tooltip>
              )}
              <SourceConversationsChip conversations={conversations} />
              {renderActivityLog && (
                <Tooltip content="Activity log">
                  <IconButton
                    icon="activity"
                    variant="tertiary"
                    active={isShowingActivity}
                    aria-label="Activity log"
                    aria-pressed={isShowingActivity}
                    onClick={() => setIsShowingActivity((shown) => !shown)}
                  />
                </Tooltip>
              )}
            </FlexBox>
          </FlexBox>

          <FlexBox flexDirection="column" gap="100">
            <Text.Heading variant="heading-3" as="h2">
              {title}
            </Text.Heading>

            {/* The pencil is a 32px icon button, so a new draft's row is that
                tall whether or not it is there. Held at the same height for an
                update draft, which has no pencil, so the breadcrumb sits the
                same distance under the title in both. */}
            <FlexBox alignItems="center" gap="100" minHeight="32px">
              {breadcrumb && (
                /* The separator belongs to the breadcrumb, not to the prompt
                   or link after it, so it stays in the subdued colour. */
                <Text.Paragraph variant="3" subdued>
                  {actions.missingFiling || articleHref
                    ? `${breadcrumb} ·`
                    : breadcrumb}
                </Text.Paragraph>
              )}

              {/* Beside the pencil that fixes it, not only under the buttons it
                  blocks, so the reviewer can see what is missing and reach for
                  the control in the same glance. */}
              {actions.missingFiling && (
                <Text.Paragraph variant="3" color="error">
                  {`Choose ${actions.missingFiling}`}
                </Text.Paragraph>
              )}

              {articleHref && (
                <Text.ExternalLink
                  href={articleHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open article in a new tab"
                  className={s.articleLink}
                >
                  Open article
                </Text.ExternalLink>
              )}

              {canRefile && (
                /* `icon` with no children renders habla's `IconButton`, and
                   `fixed` lets the panel escape the card's overflow. */
                <DropdownButton
                  icon="pen"
                  noArrow
                  fixed
                  buttonProps={{
                    variant: 'tertiary',
                    'aria-label': 'Change where this suggestion is filed',
                  }}
                  show={isFiling}
                  onToggle={setIsFiling}
                  dropdown={
                    <SuggestionFiling
                      suggestion={suggestion}
                      actions={actions}
                    />
                  }
                />
              )}
            </FlexBox>
          </FlexBox>

          {summary && (
            <Text.Paragraph variant="4" subdued>
              {summary}
            </Text.Paragraph>
          )}
        </FlexBox>

        {renderActivityLog && isShowingActivity ? (
          <Box margin="400 0 0">
            <SuggestionActivityLog
              aiDraftId={suggestion.id}
              render={renderActivityLog}
            />
          </Box>
        ) : (
          /* The body is the AI's own generated markup, served by the
             knowledge backend — the same trust boundary as the version-history
             diff and the draft modal, both of which render it the same way. */
          <div
            className={s.body}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        )}
      </Box>

      <SuggestionActions suggestion={suggestion} actions={actions} />
    </FlexBox>
  );
}
